import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import LazyMediaItem from 'components/reusable/Lazy/lazy-media-item';
import Icon from 'components/global/Icon/Primary';
import { mqSmallMin } from 'styled/tools/media-queries';

const LightboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(30px);
  position: fixed;
  inset: 0;
  z-index: 15;
`;

const LightboxBackgroundButton = styled.button`
  position: fixed;
  inset: 0;
  display: block;
  background-color: #000;
  opacity: 80%;
  appearance: none;
  border: none;
  cursor: pointer;
  z-index: -1;
`;

const LightboxMediaWrapper = styled.div`
  position: relative;
  border-radius: 25px;
  max-width: calc(100vw - 1rem);
  max-height: 60vh;
  width: 100%;
  object-fit: cover;
  overflow: hidden;

  @media ${mqSmallMin} {
    max-width: 60vh;
  }

  @media ${mqLargeMin} {
    max-width: 53.25rem;
  }
`;

const LightboxThumbnails = styled.div`
  position: relative;
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 10px;

  @media ${mqLargeMin} {
    gap: 1rem;
  }
`;

const LightboxThumbnail = styled.button`
  appearance: none;
  border: none;
  border-radius: 15px;
  height: 50px;
  width: 50px;
  overflow: hidden;
  cursor: pointer;

  @media ${mqLargeMin} {
    height: 70px;
    width: 70px;
  }
`;

const ThumbnailImage = styled.img`
  height: 100%;
  object-fit: cover;
`;

const LightboxCloseButton = styled.button`
  appearance: none;
  position: absolute;
  top: 35px;
  right: 35px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0;
  color: #7f8b97;
`;

const ProductLightbox = ({ mediaItems, lightboxItem, setLightboxItem }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!lightboxItem) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxItem(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxItem, setLightboxItem]);

  if (!mounted || !lightboxItem) return null;

  return createPortal(
    <LightboxWrapper>
      <LightboxCloseButton
        onClick={() => setLightboxItem(false)}
        aria-label='close lightbox'
      >
        <Icon name='close' size='lg' />
      </LightboxCloseButton>
      <LightboxBackgroundButton
        onClick={() => setLightboxItem(false)}
        aria-label='close lightbox'
      ></LightboxBackgroundButton>
      <LightboxMediaWrapper key={lightboxItem?.previewImage?.url}>
        <LazyMediaItem
          media={lightboxItem}
          mediaType={lightboxItem.mediaContentType}
          mediaSize='large'
        />
      </LightboxMediaWrapper>
      <LightboxThumbnails>
        {mediaItems.map((mediaItem, index) => (
          <LightboxThumbnail
            key={`lightbox-thumbnail-${index}`}
            onClick={() => setLightboxItem(mediaItem)}
            aria-label='View image in lightbox'
          >
            <ThumbnailImage
              src={mediaItem.previewImage.mediumUrl}
              alt={mediaItem.previewImage.altText}
              loading='lazy'
              decoding='async'
            />
          </LightboxThumbnail>
        ))}
      </LightboxThumbnails>
    </LightboxWrapper>,
    document.body,
  );
};

export default ProductLightbox;
